import { BIRTH_DATE_EARLIEST, BIRTH_SEX_OPTIONS, birthDateError, readBirthSex } from '@bariendo/lib/birth-details';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import { Button, Group, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { useState } from 'react';
import { PatientProfileEdit } from '@/services/patientProfile';
import { ORBIT_SETTLED } from '@/theme/colors';
import { HomeAddressControl } from './HomeAddressControl';
import { ProfileNameControl } from './ProfileNameControl';
import { Row } from './PatientAccessCallWidgets';

type ProfileFieldWidget = Extract<PacWidget, { kind: 'profileField' }>;

interface PatientProfileFieldRowProps {
  widget: ProfileFieldWidget;
  patient: Patient | undefined;
  /** Absent means read-only: a saved note, or a role that cannot write the Patient. */
  onSave?: (edit: PatientProfileEdit) => Promise<void>;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

const FULL_DATE = /^\d{4}-\d{2}-\d{2}$/;
const NOT_ON_FILE = 'Not on file';

/** The date only, no age; a partial FHIR date (year or year-month) shows as stored. */
function formatBirthDateOnly(birthDate: string | undefined): string | undefined {
  if (!birthDate) return undefined;
  return FULL_DATE.test(birthDate) ? dayjs(birthDate).format('MMM D, YYYY') : birthDate;
}

/**
 * A Patient field prefilled from the chart and edited in place (BAR-2928). An
 * edit writes the Patient record, not the note, and says so with "Saved to
 * profile". Date of birth is click-to-edit; sex assigned at birth is chips,
 * saved on tap. The legal and preferred names (BAR-2927) and the home address
 * (BAR-3001) are click-to-edit.
 */
export function PatientProfileFieldRow({ widget, patient, onSave }: PatientProfileFieldRowProps) {
  const [state, setState] = useState<SaveState>('idle');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string>();

  async function save(edit: PatientProfileEdit): Promise<boolean> {
    if (!onSave) return false;
    setState('saving');
    try {
      await onSave(edit);
      setState('saved');
      return true;
    } catch {
      setState('failed');
      return false;
    }
  }

  async function submitBirthDate(): Promise<void> {
    const invalid = birthDateError(draft);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(undefined);
    if (await save({ field: 'birthDate', value: draft })) setEditing(false);
  }

  function startEditing(): void {
    const current = patient?.birthDate;
    setDraft(current && FULL_DATE.test(current) ? current : '');
    setError(undefined);
    setState('idle');
    setEditing(true);
  }

  let control;
  if (widget.field === 'legalName' || widget.field === 'preferredName') {
    control = (
      <ProfileNameControl
        field={widget.field}
        label={widget.label}
        patient={patient}
        save={onSave ? save : undefined}
        saving={state === 'saving'}
        onStartEditing={() => setState('idle')}
      />
    );
  } else if (widget.field === 'homeAddress') {
    control = (
      <HomeAddressControl
        label={widget.label}
        patient={patient}
        save={onSave ? save : undefined}
        saving={state === 'saving'}
        onStartEditing={() => setState('idle')}
      />
    );
  } else if (widget.field === 'birthDate') {
    const shown = formatBirthDateOnly(patient?.birthDate);
    if (editing) {
      control = (
        <Group gap={4} wrap="nowrap" align="flex-start">
          <TextInput
            type="date"
            size="xs"
            aria-label={widget.label}
            value={draft}
            min={BIRTH_DATE_EARLIEST}
            max={dayjs().format('YYYY-MM-DD')}
            error={error}
            disabled={state === 'saving'}
            onChange={(event) => setDraft(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') void submitBirthDate();
              if (event.key === 'Escape') setEditing(false);
            }}
          />
          <Button size="xs" color="green.9" loading={state === 'saving'} onClick={() => void submitBirthDate()}>
            Save
          </Button>
          <Button size="xs" variant="default" disabled={state === 'saving'} onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </Group>
      );
    } else if (onSave) {
      control = (
        <UnstyledButton onClick={startEditing} aria-label={`Edit ${widget.label.toLowerCase()}`}>
          <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} td="underline dotted">
            {shown ?? NOT_ON_FILE}
          </Text>
        </UnstyledButton>
      );
    } else {
      control = (
        <Text size="sm" fw={shown ? 500 : 400} c={shown ? undefined : 'dimmed'} ta="right">
          {shown ?? NOT_ON_FILE}
        </Text>
      );
    }
  } else {
    const current = readBirthSex(patient);
    if (onSave) {
      control = (
        <Group gap={4} justify="flex-end">
          {BIRTH_SEX_OPTIONS.map((option) => {
            const isOn = option.code === current;
            return (
              <Button
                key={option.code}
                size="compact-xs"
                radius="xl"
                variant={isOn ? 'light' : 'default'}
                color={isOn ? 'green' : 'gray'}
                aria-pressed={isOn}
                disabled={state === 'saving'}
                onClick={() => {
                  if (!isOn) void save({ field: 'birthSex', value: option.code });
                }}
              >
                {option.label}
              </Button>
            );
          })}
        </Group>
      );
    } else {
      const label = BIRTH_SEX_OPTIONS.find((option) => option.code === current)?.label;
      control = (
        <Text size="sm" fw={label ? 500 : 400} c={label ? undefined : 'dimmed'} ta="right">
          {label ?? NOT_ON_FILE}
        </Text>
      );
    }
  }

  return (
    <Row label={widget.label} say={widget.say}>
      <Stack gap={2} align="flex-end">
        {control}
        {state === 'saved' && (
          <Text size="xs" c={ORBIT_SETTLED}>
            Saved to profile
          </Text>
        )}
        {state === 'failed' && (
          <Text size="xs" c="red.7" role="alert">
            Couldn&rsquo;t save to profile
          </Text>
        )}
      </Stack>
    </Row>
  );
}
